import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { ChevronDown, ChevronsUpDown, ChevronUp, Plus, X } from "lucide-react";
import { useGetPromptsQuery } from "@/store/admin_api";
import type { Prompt } from "@/model/interface/Prompt";
import CreatePromptDialog from "./CreatePromptDialog";

function SkillSummary({ skill }: { skill?: Prompt }) {
  return (
    <div className="min-w-0 flex-1">
      <p className={`text-sm truncate ${skill ? "" : "text-destructive"}`}>
        {skill?.name ?? "Missing skill (deleted from library)"}
        {skill?.archivedAt && (
          <span className="ml-1.5 text-xs text-muted-foreground">archived</span>
        )}
        {skill && skill.activeVersion != null && (
          <span className="ml-1.5 font-mono text-xs text-muted-foreground">v{skill.activeVersion}</span>
        )}
        {skill && skill.activeVersion == null && (
          <span className="ml-1.5 text-xs font-medium text-destructive">nothing published</span>
        )}
        {skill?.skillType === "PHASE" && (
          <span className="ml-2 rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-primary">
            Call stage
          </span>
        )}
      </p>
      {skill?.description && (
        <p className="text-xs text-muted-foreground truncate">{skill.description}</p>
      )}
    </div>
  );
}

function LockedSkillGroup({
  label,
  hint,
  skillIds,
  knownSkills,
  firstPosition,
}: {
  label: string;
  hint: string;
  skillIds: string[];
  knownSkills: Prompt[];
  firstPosition: number;
}) {
  if (skillIds.length === 0) {
    return null;
  }
  return (
    <div className="space-y-1 max-w-lg">
      <p className="text-xs text-muted-foreground">
        <span className="font-medium text-foreground">{label}</span> · {hint}
      </p>
      {skillIds.map((skillId, index) => (
        <div key={skillId} className="flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-2">
          <span className="font-mono text-xs text-muted-foreground w-4 shrink-0">{firstPosition + index}</span>
          <SkillSummary skill={knownSkills.find((knownSkill) => knownSkill.id === skillId)} />
        </div>
      ))}
    </div>
  );
}

export default function SkillsPicker({
  selectedSkillIds,
  onChange,
  inheritedSkillIds = [],
  ownGroupLabel,
  ownGroupHint,
}: {
  selectedSkillIds: string[];
  onChange: (skillIds: string[]) => void;
  inheritedSkillIds?: string[];
  ownGroupLabel: string;
  ownGroupHint: string;
}) {
  const { data: skillPage } = useGetPromptsQuery({ kind: "SKILL", size: 200 });
  const { data: archivedSkillPage } = useGetPromptsQuery({ kind: "SKILL", archived: true, size: 200 });
  const [createOpen, setCreateOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  const activeSkills = skillPage?.items ?? [];
  const knownSkills = [...activeSkills, ...(archivedSkillPage?.items ?? [])];
  const globalSkillIds = activeSkills
    .filter((skill) => skill.skillAudience === "GLOBAL")
    .map((skill) => skill.id);
  const addableSkills = activeSkills.filter(
    (skill) =>
      skill.skillAudience !== "GLOBAL" &&
      !selectedSkillIds.includes(skill.id) &&
      !inheritedSkillIds.includes(skill.id),
  );
  const firstOwnPosition = globalSkillIds.length + inheritedSkillIds.length + 1;

  const addSkill = (skillId: string) => {
    if (!selectedSkillIds.includes(skillId)) {
      onChange([...selectedSkillIds, skillId]);
    }
  };

  const moveSkill = (index: number, offset: number) => {
    const reordered = [...selectedSkillIds];
    [reordered[index], reordered[index + offset]] = [reordered[index + offset], reordered[index]];
    onChange(reordered);
  };

  const handleCreated = (created: Prompt) => {
    if (created.skillAudience !== "GLOBAL") {
      addSkill(created.id);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 max-w-lg">
        <div className="flex-1">
          <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                role="combobox"
                aria-expanded={pickerOpen}
                className="w-full justify-between font-normal text-muted-foreground"
              >
                Add skill...
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[380px] p-0" align="start">
              <Command>
                <CommandInput placeholder="Search skills" />
                <CommandList>
                  <CommandEmpty>No skills found.</CommandEmpty>
                  <CommandGroup>
                    {addableSkills.map((skill) => (
                      <CommandItem
                        key={skill.id}
                        value={`${skill.name} ${skill.description ?? ""} ${skill.id}`}
                        onSelect={() => {
                          addSkill(skill.id);
                          setPickerOpen(false);
                        }}
                      >
                        <div className="flex min-w-0 flex-col">
                          <span className="truncate">{skill.name}</span>
                          {skill.description && (
                            <span className="truncate text-xs text-muted-foreground">{skill.description}</span>
                          )}
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
        <Button type="button" size="sm" variant="outline" className="gap-1.5" onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4" /> New skill
        </Button>
      </div>

      <LockedSkillGroup
        label="Global"
        hint="every agent gets these"
        skillIds={globalSkillIds}
        knownSkills={knownSkills}
        firstPosition={1}
      />
      <LockedSkillGroup
        label="From template"
        hint="shared by every agent on this template"
        skillIds={inheritedSkillIds}
        knownSkills={knownSkills}
        firstPosition={globalSkillIds.length + 1}
      />

      {selectedSkillIds.length > 0 && (
        <div className="space-y-1 max-w-lg">
          <p className="text-xs text-muted-foreground">
            <span className="font-medium text-foreground">{ownGroupLabel}</span> · {ownGroupHint}
          </p>
          {selectedSkillIds.map((skillId, index) => {
            const skill = knownSkills.find((knownSkill) => knownSkill.id === skillId);
            return (
              <div key={skillId} className="flex items-center gap-2 rounded-md border bg-background px-3 py-2">
                <span className="font-mono text-xs text-muted-foreground w-4 shrink-0">{firstOwnPosition + index}</span>
                <SkillSummary skill={skill} />
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-7 w-7 p-0 shrink-0"
                  disabled={index === 0}
                  aria-label={`Move ${skill?.name ?? "skill"} earlier`}
                  onClick={() => moveSkill(index, -1)}
                >
                  <ChevronUp className="h-3.5 w-3.5" />
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-7 w-7 p-0 shrink-0"
                  disabled={index === selectedSkillIds.length - 1}
                  aria-label={`Move ${skill?.name ?? "skill"} later`}
                  onClick={() => moveSkill(index, 1)}
                >
                  <ChevronDown className="h-3.5 w-3.5" />
                </Button>
                <button
                  type="button"
                  className="text-muted-foreground hover:text-foreground shrink-0"
                  aria-label={`Remove ${skill?.name ?? "skill"}`}
                  onClick={() => onChange(selectedSkillIds.filter((selectedSkillId) => selectedSkillId !== skillId))}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      <CreatePromptDialog kind="SKILL" open={createOpen} onOpenChange={setCreateOpen} onCreated={handleCreated} />
    </div>
  );
}
